<div class="map-container" [class.fullscreen-mode]="isFullscreen">
  <aside class="fleet-panel">
    <div class="panel-header">
      <div>
        <p class="eyebrow">{{ getMapEyebrow() }}</p>
        <h2>{{ getMapHeading() }}</h2>
      </div>
      <button (click)="showAllVehicles()" class="icon-button" aria-label="Show all vehicles">
        <i class="material-icons">center_focus_strong</i>
      </button>
    </div>

    <div class="summary-grid" *ngIf="!isPassengerMap">
      <div class="summary-card">
        <strong>{{ fleetSummary.total }}</strong>
        <span>Total</span>
      </div>
      <div class="summary-card moving">
        <strong>{{ fleetSummary.moving }}</strong>
        <span>Moving</span>
      </div>
      <div class="summary-card idle">
        <strong>{{ fleetSummary.idle }}</strong>
        <span>Idle</span>
      </div>
      <div class="summary-card stale">
        <strong>{{ fleetSummary.stale }}</strong>
        <span>Stale</span>
      </div>
      <div class="summary-card no-signal">
        <strong>{{ fleetSummary.noSignal }}</strong>
        <span>No signal</span>
      </div>
    </div>

    <div class="tracking-alert loading" *ngIf="isLoading">
      <i class="material-icons">sync</i>
      {{ isPassengerMap ? 'Loading your booked trips...' : 'Loading vehicles and live tracking...' }}
    </div>

    <div class="tracking-alert error" *ngIf="trackingError">
      <i class="material-icons">warning</i>
      {{ trackingError }}
    </div>

    <label class="search-field" *ngIf="!isPassengerMap">
      <i class="material-icons">search</i>
      <input [(ngModel)]="searchTerm" type="search" placeholder="Search registration, model, or label">
    </label>

    <div class="filter-row" *ngIf="!isPassengerMap" aria-label="Fleet status filters">
      <button [class.active]="statusFilter === 'all'" (click)="statusFilter = 'all'">All</button>
      <button [class.active]="statusFilter === 'moving'" (click)="statusFilter = 'moving'">Moving</button>
      <button [class.active]="statusFilter === 'idle'" (click)="statusFilter = 'idle'">Idle</button>
      <button [class.active]="statusFilter === 'stale'" (click)="statusFilter = 'stale'">Stale</button>
      <button [class.active]="statusFilter === 'noSignal'" (click)="statusFilter = 'noSignal'">No signal</button>
    </div>

    <div class="vehicle-list">
      <button *ngFor="let vehicle of getFilteredVehicles()" (click)="selectVehicle(vehicle)" class="vehicle-row">
        <span class="vehicle-dot" [class]="getVehicleStatus(vehicle)"></span>
        <span>
          <strong>{{ vehicle.vehicleRegistration || vehicle.vehicleLabel || ('Vehicle ' + vehicle.vehicleID) }}</strong>
          <small>{{ vehicle.vehicleManufacturerMake }} {{ vehicle.vehicleManufacturerModel }} · {{ getVehicleStatusLabel(vehicle) }}</small>
        </span>
        <i class="material-icons">chevron_right</i>
      </button>

      <div class="empty-list" *ngIf="!isLoading && getFilteredVehicles().length === 0">
        {{ isPassengerMap ? 'No booked trip vehicles are available to track.' : 'No vehicles match the selected filters.' }}
      </div>
    </div>
  </aside>

  <div class="map-stage">
    <div class="map-toolbar">
      <div>
        <strong>{{ getMapToolbarTitle() }}</strong>
        <span *ngIf="lastUpdated">Updated {{ lastUpdated | date:'shortTime' }}</span>
      </div>
      <div class="toolbar-actions">
        <button (click)="showAllVehicles()" class="toolbar-button">
          <i class="material-icons">zoom_out_map</i>
          <span>{{ isPassengerMap ? 'Fit trip' : 'Fit fleet' }}</span>
        </button>
        <button (click)="toggleFullscreen()" class="toolbar-button fullscreen-button">
          <i class="material-icons">{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</i>
          <span>{{ isFullscreen ? 'Exit full screen' : 'Full screen' }}</span>
        </button>
      </div>
    </div>

    <div id="map" class="map"></div>

    <section class="vehicle-detail">
      <div>
        <p class="eyebrow">Selected vehicle</p>
        <h3>{{ getSelectedVehicleTitle() }}</h3>
        <p>{{ getSelectedVehicleSubtitle() }}</p>
      </div>
      <div class="detail-metrics" *ngIf="selectedTracking">
        <span><strong>{{ selectedTracking.vehicleTrackingSpeed | number:'1.0-0' }}</strong> km/h</span>
        <span><strong>{{ selectedTracking.vehicleTrackingSatelites }}</strong> satellites</span>
        <span><strong>{{ selectedTracking.vehicleTrackingDateTime | date:'shortTime' }}</strong> last ping</span>
      </div>
    </section>
  </div>

  <aside class="passenger-panel">
    <div class="panel-header compact">
      <div>
        <p class="eyebrow">Passenger glance</p>
        <h2>Trip Info</h2>
      </div>
    </div>

    <ng-container *ngIf="!isPassengerMap">
      <div class="passenger-card" *ngFor="let item of passengerHighlights">
        <i class="material-icons">{{ item.icon }}</i>
        <span>
          <strong>{{ item.value }}</strong>
          <small>{{ item.label }}</small>
        </span>
      </div>
    </ng-container>

    <ng-container *ngIf="isPassengerMap">
      <div class="tracking-alert error" *ngIf="getPassengerEmptyState()">
        <i class="material-icons">info</i>
        {{ getPassengerEmptyState() }}
      </div>

      <button *ngFor="let trip of passengerTrips" type="button" class="trip-row" [class.active]="selectedPassengerTrip?.bookingID === trip.bookingID" (click)="selectPassengerTrip(trip)">
        <span>
          <strong>{{ getPassengerTripTitle(trip) }}</strong>
          <small>{{ trip.bookingReference || getPassengerOperator(trip) }}</small>
        </span>
        <i class="material-icons">chevron_right</i>
      </button>

      <section class="trip-detail-card" *ngIf="selectedPassengerTrip">
        <p class="eyebrow">Booked trip</p>
        <h3>{{ getPassengerTripTitle(selectedPassengerTrip) }}</h3>
        <dl>
          <div>
            <dt>Operator</dt>
            <dd>{{ getPassengerOperator(selectedPassengerTrip) }}</dd>
          </div>
          <div>
            <dt>Trip time</dt>
            <dd>{{ selectedPassengerTrip.trip?.tripStartTime | date:'medium' }} - {{ selectedPassengerTrip.trip?.tripEndTime | date:'shortTime' }}</dd>
          </div>
          <div>
            <dt>Route</dt>
            <dd>{{ getPassengerRouteName(selectedPassengerTrip) }}</dd>
          </div>
          <div>
            <dt>Next stop</dt>
            <dd>{{ getNextStopName(selectedPassengerTrip) }}</dd>
          </div>
          <div>
            <dt>ETA</dt>
            <dd>{{ getNextStopEta(selectedPassengerTrip) }}</dd>
          </div>
        </dl>

        <div class="stop-list" *ngIf="getOrderedStops(selectedPassengerTrip).length">
          <p class="eyebrow">Stops</p>
          <ol>
            <li *ngFor="let stop of getOrderedStops(selectedPassengerTrip)">
              <span>{{ stop.stopOrder || '-' }}</span>
              <strong>{{ stop.stopName || 'Stop' }}</strong>
            </li>
          </ol>
        </div>
      </section>
    </ng-container>

    <div class="map-legend" *ngIf="!isPassengerMap">
      <p class="eyebrow">Map legend</p>
      <span><i class="legend-dot moving"></i> Moving vehicle</span>
      <span><i class="legend-dot idle"></i> Idle vehicle</span>
      <span><i class="legend-dot stale"></i> Stale GPS update</span>
      <span><i class="legend-dot no-signal"></i> No tracking returned</span>
    </div>

    <div class="map-legend" *ngIf="isPassengerMap">
      <p class="eyebrow">Map legend</p>
      <span><i class="legend-dot moving"></i> Booked vehicle</span>
      <span><i class="legend-line planned"></i> Planned route</span>
      <span><i class="legend-line travelled"></i> Travelled route</span>
    </div>
  </aside>
</div>
